import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '../../components/Brand'
import { getCollectionsUser, number, request, titleize } from '../api'
import { priorityOperationStorageKey } from '../imports'
import {
  derivePriorityPresentation,
  formatAbsoluteTime,
  formatRelativeTime,
  legacyPriorityAvailability,
  manualSyncControl,
  pollDelay,
  pollObservation,
  receiptCapabilityLabel,
  safeErrorLabel,
  shortCorrelation,
} from '../priorityIntegration'
import useResource from '../useResource'
import { Badge, SectionTitle, StatePanel } from './Common'
import PriorityUploadIntegration from './PriorityUploadIntegration'
import PrioritySnapshotReconciliation from './PrioritySnapshotReconciliation'

const SYNC_REQUEST_ERRORS = {
  invalid_request: 'The synchronization request was invalid.',
  unauthenticated: 'Sign in again before requesting a synchronization.',
  forbidden: 'The server did not grant this session permission to synchronize Priority.',
  integration_disabled: 'Priority synchronization is disabled by deployment configuration.',
  configuration_incomplete: 'Priority synchronization is unavailable until configuration is complete.',
  rate_limited: 'A recent manual request is still within the server rate limit.',
  trigger_unavailable: 'The synchronization trigger is temporarily unavailable.',
}

function TimeValue({ value, serverTime, never = 'Never' }) {
  if (!value) return <span>{never}</span>
  return <time dateTime={value} title={formatAbsoluteTime(value)}>{formatRelativeTime(value, serverTime)}</time>
}

function Correlation({ value }) {
  const short = shortCorrelation(value)
  return short ? <code className="col-priority-correlation" title={value} aria-label={`Correlation ${value}`}>{short}…</code> : <span>—</span>
}

function DetailRow({ label, children }) {
  return <div><dt>{label}</dt><dd>{children ?? '—'}</dd></div>
}

function statusUnavailableError() {
  return new Error('Priority integration status is unavailable. Existing Collections data has not been cleared.')
}

export default function PriorityIntegration({ user, onSyncCompleted, onWorkspaceReset, businessUnit = '' }) {
  const resource = useResource('integrations/priority/status')
  const statusEndpointMissing = resource.error?.status === 404
  const legacyHealth = useResource('health', {}, statusEndpointMissing)
  const [lastStatus, setLastStatus] = useState(null)
  const [dismissedReconciliation, setDismissedReconciliation] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [reloadingFixtures, setReloadingFixtures] = useState(false)
  const [polling, setPolling] = useState(null)
  const [announcement, setAnnouncement] = useState('')
  const [actionError, setActionError] = useState(null)
  const polledJob = useRef(null)
  const confirmAction = useRef(null)

  useEffect(() => {
    if (resource.data) setLastStatus(resource.data)
  }, [resource.data])

  useEffect(() => {
    if (confirming) confirmAction.current?.focus()
  }, [confirming])

  const legacyFallback = useMemo(
    () => legacyPriorityAvailability(legacyHealth.data, {
      endpointMissing: statusEndpointMissing,
      loading: legacyHealth.loading,
    }),
    [legacyHealth.data, legacyHealth.loading, statusEndpointMissing],
  )
  const fallbackStatus = legacyFallback.status
  const status = resource.data || (statusEndpointMissing ? fallbackStatus : lastStatus)
  const statusError = statusEndpointMissing ? legacyHealth.error : resource.error
  const statusLoading = statusEndpointMissing ? legacyHealth.loading : resource.loading
  const retryStatus = statusEndpointMissing ? legacyHealth.retry : resource.retry
  const presentation = useMemo(
    () => derivePriorityPresentation(status, { unavailable: Boolean(!status && (statusError || legacyFallback.unavailable)) }),
    [legacyFallback.unavailable, statusError, status],
  )
  const syncControl = useMemo(() => manualSyncControl(status, user), [status, user])

  useEffect(() => {
    if (!presentation.active || polling || !status) return
    const correlationId = status.sync?.active?.correlation_id || status.sync?.last_attempt?.correlation_id || null
    const jobKey = correlationId || `${presentation.state}-${status.sync?.active?.started_at || ''}`
    if (polledJob.current === jobKey) return
    polledJob.current = jobKey
    setPolling({ correlationId, startedAt: Date.now(), observed: true })
    setAnnouncement(presentation.state === 'queued' ? 'Priority synchronization is queued.' : 'Priority synchronization is running.')
  }, [polling, presentation.active, presentation.state, status])

  useEffect(() => {
    if (!polling || statusLoading) return undefined
    const observation = pollObservation(status, polling.correlationId, polling.observed)
    if (observation.complete) {
      setPolling(null)
      if (observation.failed) {
        setAnnouncement('Priority synchronization failed. The last complete snapshot remains available.')
      } else {
        setAnnouncement('Priority synchronization completed successfully.')
        onSyncCompleted?.()
        retryStatus()
      }
      return undefined
    }

    const elapsed = Date.now() - polling.startedAt
    const delay = pollDelay(elapsed)
    if (delay === null) {
      setPolling(null)
      setAnnouncement('Automatic status polling stopped after two minutes. Refresh status to continue checking.')
      return undefined
    }
    if (observation.observed !== polling.observed) {
      setPolling((current) => current ? { ...current, observed: observation.observed } : current)
    }
    const timer = setTimeout(() => retryStatus(), delay)
    return () => clearTimeout(timer)
  }, [onSyncCompleted, polling, retryStatus, status, statusLoading])

  const startPolling = (correlationId, message) => {
    polledJob.current = correlationId || 'manual-request'
    setPolling({ correlationId: correlationId || null, startedAt: Date.now(), observed: false })
    setAnnouncement(message)
    retryStatus()
  }

  const runSync = async () => {
    setSubmitting(true)
    setActionError(null)
    try {
      const accepted = await request('integrations/priority/sync', { method: 'POST', body: {} })
      setConfirming(false)
      startPolling(accepted?.correlation_id, 'Read-only Priority synchronization accepted and queued.')
    } catch (error) {
      setConfirming(false)
      if (error.status === 409 && error.code === 'already_running') {
        const correlationId = error.data?.correlation_id || error.data?.detail?.correlation_id || null
        startPolling(correlationId, 'A Priority synchronization is already running. Following its status.')
      } else {
        setActionError(SYNC_REQUEST_ERRORS[error.code] || 'The read-only synchronization request could not be accepted.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const reloadFixtures = async () => {
    setReloadingFixtures(true)
    setActionError(null)
    try {
      await request('demo/sync', { method: 'POST', body: {} })
      setAnnouncement('Local demo fixtures reloaded. No Priority tenant was contacted.')
      retryStatus()
      onSyncCompleted?.()
    } catch {
      setActionError('The local demo fixtures could not be reloaded.')
    } finally {
      setReloadingFixtures(false)
    }
  }

  const refreshStatus = () => {
    if (!polling) polledJob.current = null
    retryStatus()
  }

  if (resource.data?.mode === 'uploaded_snapshot') {
    return <PriorityUploadIntegration businessUnit={businessUnit} resource={resource} onCompleted={onSyncCompleted} onReset={onWorkspaceReset} />
  }

  if ((resource.loading || statusEndpointMissing && legacyHealth.loading) && !status) return <StatePanel loading />
  if (!status) {
    return <StatePanel error={statusUnavailableError()} onRetry={retryStatus} />
  }

  const sync = status?.sync || {}
  const lastAttempt = sync.last_attempt
  const lastSuccess = sync.last_success
  const active = sync.active
  const company = status?.company?.label || status?.company?.id || (status?.mode === 'fixture' ? 'Local demo fixtures' : 'Not reported')
  const receiptCapability = receiptCapabilityLabel(status?.capabilities?.receipt_write)
  const latestError = safeErrorLabel(lastAttempt?.error_code)
  const role = titleize(user?.role || 'user')

  return <div className="col-priority-stack">
    {statusError && <div className="col-inline-alert" role="alert"><Icon name="cloud_off" />The latest status refresh failed. Showing the last status received from Collections.</div>}
    {actionError && <div className="col-inline-alert" role="alert"><Icon name="error" />{actionError}</div>}
    <div className="col-priority-live-region" aria-live="polite" aria-atomic="true">{announcement}</div>

    <section className={`col-card col-priority-hero ${presentation.tone}`}>
      <div className="col-priority-heading">
        <div className="col-priority-title-row"><Badge tone={presentation.tone}>{presentation.modeLabel}</Badge><span>{company}</span></div>
        <h2>{presentation.headline}</h2>
        <p>{presentation.summary}</p>
        <div className="col-priority-version-line">
          {status?.versions ? <>Priority {status.versions.priority || 'Not checked'} · OData {status.versions.odata || 'Not checked'}</> : 'Versions not checked'}
          <span>Mapping {status?.configuration?.mapping === 'not_applicable' ? 'not required for fixtures' : titleize(status?.configuration?.mapping || 'not checked')}</span>
        </div>
      </div>
      <div className="col-priority-actions">
        <button className="col-btn secondary" disabled={statusLoading} onClick={refreshStatus}><Icon name="refresh" />{statusLoading ? 'Refreshing…' : 'Refresh status'}</button>
        {status?.mode === 'fixture' && <button className="col-btn secondary" disabled={reloadingFixtures} onClick={reloadFixtures}><Icon name="refresh" />{reloadingFixtures ? 'Reloading…' : 'Reload demo data'}</button>}
        {syncControl.visible && <button className="col-btn" disabled={!syncControl.enabled || submitting} onClick={() => setConfirming(true)}><Icon name="sync" />{presentation.active ? 'Sync in progress' : submitting ? 'Requesting…' : 'Sync now'}</button>}
        {!syncControl.visible && <small>{syncControl.explanation}</small>}
        <span>Dashboard role: {role}. Server capabilities remain authoritative.</span>
      </div>
    </section>

    {confirming && <section className="col-card col-priority-confirm" role="alertdialog" aria-labelledby="priority-sync-confirm-title" aria-describedby="priority-sync-confirm-description" onKeyDown={(event) => { if (event.key === 'Escape' && !submitting) setConfirming(false) }}>
      <div><Icon name="sync" /><span><h2 id="priority-sync-confirm-title">Run a read-only Priority synchronization for {company}?</h2><p id="priority-sync-confirm-description">No accounting writes will be performed. The Collections API authorized this session; the dashboard role is display-only.</p></span></div>
      <footer><button className="col-btn secondary" disabled={submitting} onClick={() => setConfirming(false)}>Cancel</button><button ref={confirmAction} className="col-btn" disabled={submitting} onClick={runSync}>{submitting ? 'Requesting…' : 'Run sync'}</button></footer>
    </section>}

    {presentation.latestFailed && <div className="col-priority-warning" role="alert"><Icon name="warning" /><span><b>Latest sync failed{latestError ? ` · ${latestError}` : ''}</b><small>Last complete data: <TimeValue value={lastSuccess?.completed_at} serverTime={status.server_time} never="No completed snapshot" />{lastAttempt?.correlation_id && <> · correlation <Correlation value={lastAttempt.correlation_id} /></>}</small></span></div>}

    <div className="col-priority-grid">
      <section className="col-card">
        <SectionTitle title="Data freshness" text="Calculated by the Collections API" action={<Badge tone={presentation.latestFailed ? 'danger' : presentation.freshnessLabel === 'Fresh' ? 'success' : 'warning'}>{presentation.freshnessLabel}</Badge>} />
        <dl className="col-priority-details">
          <DetailRow label="Last success"><TimeValue value={lastSuccess?.completed_at} serverTime={status.server_time} never={status.contract === 'legacy_fixture_health' ? 'Timestamp not exposed by current API' : 'Never synchronized'} /></DetailRow>
          <DetailRow label="Latest attempt">{lastAttempt ? <>{titleize(lastAttempt.result || sync.state)} · <TimeValue value={lastAttempt.completed_at || lastAttempt.started_at} serverTime={status.server_time} never="Time not reported" /></> : 'No attempt reported'}</DetailRow>
          <DetailRow label="Stale threshold">{sync.stale_after_seconds ? `${number(sync.stale_after_seconds / 3600, 1)} hours` : 'Not reported'}</DetailRow>
          <DetailRow label="Authentication">{titleize(status.configuration?.auth || 'not checked')}</DetailRow>
        </dl>
      </section>
      <section className="col-card">
        <SectionTitle title="Schedule" text="Next run is shown only when supplied by the scheduler" />
        <dl className="col-priority-details">
          <DetailRow label="Interval">{status.configuration?.schedule_interval_hours ? `Every ${number(status.configuration.schedule_interval_hours)} hours` : 'Not scheduled'}</DetailRow>
          <DetailRow label="Next run"><TimeValue value={sync.next_scheduled_at} serverTime={status.server_time} never="Not supplied" /></DetailRow>
          <DetailRow label="Manual sync">{syncControl.visible ? 'Authorized by server for this session' : 'Unavailable for this session'}</DetailRow>
          <DetailRow label="Read capability">{status.capabilities?.read ? 'Available' : 'Unavailable'}</DetailRow>
        </dl>
      </section>
    </div>

    {presentation.active && <section className="col-card col-priority-active">
      <SectionTitle title={presentation.state === 'queued' ? 'Queued synchronization' : 'Running synchronization'} text="This read-only job does not perform accounting writes" action={<span className="col-spinner small" />} />
      <dl className="col-priority-details horizontal">
        <DetailRow label="Started"><TimeValue value={active?.started_at} serverTime={status.server_time} never={presentation.state === 'queued' ? 'Waiting to start' : 'Start time not reported'} /></DetailRow>
        <DetailRow label="Correlation"><Correlation value={active?.correlation_id || polling?.correlationId} /></DetailRow>
        <DetailRow label="Polling">{polling ? 'Automatic · bounded to 2 minutes' : 'Stopped'}</DetailRow>
      </dl>
    </section>}

    <section className="col-card">
      <SectionTitle title="Last complete snapshot" text={lastSuccess ? 'Counts update only after a complete successful synchronization' : 'No completed snapshot is available'} />
      {lastSuccess ? <div className="col-priority-counts">
        <span><b>{number(lastSuccess.customers_synced)}</b> customers</span>
        <span><b>{number(lastSuccess.invoices_synced)}</b> invoices</span>
        <span><b>{number(lastSuccess.invoices_open)}</b> open</span>
        <span><b>{number(lastSuccess.invoices_closed)}</b> closed</span>
        <span><b>{lastSuccess.duration_seconds == null ? '—' : `${number(lastSuccess.duration_seconds, 1)}s`}</b> duration</span>
        <span>Correlation <Correlation value={lastSuccess.correlation_id} /></span>
      </div> : <div className="col-inline-empty">No completed {status.mode === 'fixture' ? 'demo fixture load' : 'Priority synchronization'} yet.</div>}
    </section>

    {lastAttempt?.reconciliation_id && dismissedReconciliation !== lastAttempt.reconciliation_id && <PrioritySnapshotReconciliation
      conflict={lastAttempt} businessUnit={businessUnit}
      storageKey={priorityOperationStorageKey(`${globalThis.location?.origin || 'local'}:${status.workspace_id || 'local'}`, getCollectionsUser())}
      onResolved={() => { setDismissedReconciliation(lastAttempt.reconciliation_id); retryStatus(); onSyncCompleted?.() }}
      onDiscard={() => setDismissedReconciliation(lastAttempt.reconciliation_id)}
    />}

    <section className="col-card col-priority-accounting">
      <SectionTitle title="Accounting actions" action={<Badge tone={receiptCapability === 'Stubbed' ? 'neutral' : receiptCapability === 'Disabled' ? 'warning' : 'purple'}>Receipt write-back: {receiptCapability}</Badge>} />
      <p>{status.mode === 'fixture' ? 'Receipt behavior is synthetic and stubbed.' : 'Receipt write-back is managed by deployment controls and Priority specialist approval.'} This dashboard provides status only and has no control to enable writes.</p>
    </section>
  </div>
}
